/**
 * CacheManager - Simple in-memory cache with TTL support
 * 
 * Provides singleton cache instance for managing cached Firestore data.
 * Features:
 * - TTL-based expiration
 * - Automatic cleanup of expired entries
 * - Statistics tracking (hits, misses, sets)
 * - Per-collection cache control
 * - Debug logging via errorHandler
 * 
 * Usage:
 *   CacheManager.set('products', 'pejas', productData);
 *   const data = CacheManager.get('products', 'pejas');
 *   CacheManager.invalidate('products', 'pejas');
 *   CacheManager.clear('products');
 *   CacheManager.getStats();
 */

import { getCacheTTL, isCacheable } from './CacheConfig.js';
import errorHandler from '../handlers/ErrorHandler.js';

/**
 * CacheManager class - manages in-memory cache with TTL
 * 
 * Exported as a singleton instance at the bottom of this file.
 * Access via: import CacheManager from './CacheManager.js'
 */
class CacheManager {
    constructor() {
        // Map<cacheKey, { data, timestamp, ttl }>
        this.cache = new Map();
        
        // Statistics
        this.stats = {
            hits: 0,
            misses: 0,
            sets: 0,
            clears: 0,
            invalidations: 0
        };
    }

    /**
     * Generate a cache key from collection name and identifier
     * 
     * For simple document IDs: "collectionName:docId"
     * For query objects: "collectionName:query:base64(queryJson)"
     * 
     * @param {string} collectionName - Collection name
     * @param {string|Object} identifier - Document ID or query object
     * @returns {string} Cache key
     * @private
     */
    #generateKey(collectionName, identifier) {
        if (typeof identifier === 'string') {
            return `${collectionName}:${identifier}`;
        }
        // For queries, generate key from collection + query params
        const queryStr = JSON.stringify(identifier);
        return `${collectionName}:query:${btoa(queryStr)}`;
    }

    /**
     * Check if a cache entry is still valid (not expired)
     * 
     * @param {Object} entry - Cache entry { data, timestamp, ttl }
     * @returns {boolean} True if entry is valid (not expired)
     * @private
     */
    #isEntryValid(entry) {
        const now = Date.now();
        return (now - entry.timestamp) < entry.ttl;
    }

    /**
     * Get value from cache if valid and not expired
     * 
     * Expired entries are automatically removed from cache.
     * Updates hit/miss statistics.
     * 
     * @param {string} collectionName - Collection name
     * @param {string|Object} identifier - Document ID or query object
     * @returns {*|null} Cached value or null if not found/expired
     * 
     * @example
     * const data = CacheManager.get('products', 'pejas');
     * if (data) { console.log('Cache hit!', data); }
     * 
     * const results = CacheManager.get('products', { whereField: 'active', whereValue: true });
     */
    get(collectionName, identifier) {
        const key = this.#generateKey(collectionName, identifier);
        const entry = this.cache.get(key);

        if (!entry) {
            this.stats.misses++;
            return null;
        }

        if (!this.#isEntryValid(entry)) {
            // Expired entry - remove and count as miss
            this.cache.delete(key);
            this.stats.misses++;
            return null;
        }

        // Cache hit!
        this.stats.hits++;
        return entry.data;
    }

    /**
     * Set value in cache with TTL from configuration
     * 
     * Non-cacheable collections (TTL = 0) are silently skipped.
     * Updates set statistics.
     * 
     * @param {string} collectionName - Collection name
     * @param {string|Object} identifier - Document ID or query object
     * @param {*} data - Data to cache
     * 
     * @example
     * CacheManager.set('products', 'pejas', { id: 'pejas', name: 'Pejas', ... });
     * CacheManager.set('products', { whereField: 'active' }, [ {...}, {...} ]);
     */
    set(collectionName, identifier, data) {
        if (!isCacheable(collectionName)) {
            return; // Don't cache uncacheable collections
        }

        const key = this.#generateKey(collectionName, identifier);
        const ttl = getCacheTTL(collectionName);

        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl
        });

        this.stats.sets++;
    }

    /**
     * Invalidate a specific cache entry
     * 
     * Removes the entry immediately without waiting for TTL expiration.
     * Useful when data is known to be stale (after a write).
     * 
     * @param {string} collectionName - Collection name
     * @param {string|Object} identifier - Document ID or query object
     * 
     * @example
     * // After updating a product
     * CacheManager.invalidate('products', 'pejas');
     * 
     * // After modifying a query result
     * CacheManager.invalidate('products', { whereField: 'active' });
     */
    invalidate(collectionName, identifier) {
        const key = this.#generateKey(collectionName, identifier);
        if (this.cache.delete(key)) {
            this.stats.invalidations++;
        }
    }

    /**
     * Clear all cache entries for a specific collection
     * 
     * Useful for forcing cache refresh of an entire collection.
     * Updates clear statistics.
     * 
     * @param {string} collectionName - Collection name to clear
     * 
     * @example
     * // Clear products cache
     * CacheManager.clear('products');
     * 
     * // Access from console: window.CacheManager.clear('products')
     */
    clear(collectionName) {
        let cleared = 0;
        for (const [key] of this.cache) {
            if (key.startsWith(collectionName)) {
                this.cache.delete(key);
                cleared++;
            }
        }
        this.stats.clears++;
    }

    /**
     * Clear all cache entries
     * 
     * Nuclear option - removes all cached data.
     * Useful for debugging or forcing complete refresh.
     * 
     * @example
     * CacheManager.clearAll();
     * 
     * // Access from console: window.CacheManager.clearAll()
     */
    clearAll() {
        const count = this.cache.size;
        this.cache.clear();
        this.stats.clears++;
    }

    /**
     * Get cache statistics
     * 
     * Returns hit rate percentage and current cache size.
     * Useful for monitoring cache effectiveness.
     * 
     * @returns {Object} Statistics object
     * @example
     * const stats = CacheManager.getStats();
     * console.log(stats);
     * // Output: {
     * //   hits: 45,
     * //   misses: 12,
     * //   sets: 15,
     * //   clears: 2,
     * //   invalidations: 3,
     * //   size: 8,
     * //   hitRate: "78.9%"
     * // }
     * 
     * // Access from console: window.CacheManager.getStats()
     */
    getStats() {
        const total = this.stats.hits + this.stats.misses;
        const hitRate = total > 0 ? (this.stats.hits / total * 100).toFixed(1) : 0;
        return {
            ...this.stats,
            size: this.cache.size,
            hitRate: `${hitRate}%`
        };
    }

    /**
     * Reset statistics counters
     * 
     * Useful for benchmarking specific operations.
     * Does not clear cached data.
     * 
     * @example
     * CacheManager.resetStats();
     * // ... perform operations ...
     * const stats = CacheManager.getStats();
     */
    resetStats() {
        this.stats = {
            hits: 0,
            misses: 0,
            sets: 0,
            clears: 0,
            invalidations: 0
        };
    }
}

// Export singleton instance
export default new CacheManager();
